import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon, DocumentTextIcon, TicketIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_DAYPASS_REPORT } from 'graphql/reports/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import loadingImage from '../../assets/images/boat-loading.gif'
import { Divider } from 'components'
import i18n from 'i18next'

const INITIAL_FILTER_STATE = {
    travel_date: {
        start_date: moment().subtract(3, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
}

export default function DayPassReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [filterApplied, setFilterApplied] = useState(false)
    const [totalValues, setTotalValues] = useState(null)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
            setTotalValues(null)
        }
    }, [])

    const [getDayPassReport, { loading: reportLoading }] = useLazyQuery(GET_DAYPASS_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.daypassReport.success && data.daypassReport.code === '200') {
                setData(data.daypassReport.daypass_report)
                setTotalRows(data.daypassReport.total_count)
                setTotalValues(data.daypassReport.total)
            } else {
                handleError(data.daypassReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch day pass report'))
        setShowErrDialog(true)
    }

    const fetchDayPassReport = (page, filterData = filterObj, limit = perPage) => {
        const offset = (page - 1) * limit
        getDayPassReport({
            variables: {
                page: {
                    limit,
                    offset,
                },
                travel_date_range: {
                    start_date: filterData.travel_date.start_date,
                    end_date: filterData.travel_date.end_date,
                },
            },
        })
    }

    useEffect(() => {
        if (filterApplied) {
            fetchDayPassReport(currentPage, filterObj, perPage)
        }
    }, [currentPage, perPage, filterApplied])

    const handleFilter = () => {
        setCurrentPage(1)
        setFilterApplied(true)
        fetchDayPassReport(1, filterObj)
    }

    const handleClear = () => {
        const resetFilter = {
            travel_date: {
                start_date: moment().subtract(3, 'months').format('YYYY-MM-DD'),
                end_date: moment().format('YYYY-MM-DD'),
            },
        }
        setFilterObj(resetFilter)
        setCurrentPage(1)
        setPerPage(10)
        setFilterApplied(false)
        setData([]) // Clear existing data
        setTotalValues(null) // Clear total values
    }

    const handlePageChange = page => {
        if (filterApplied) {
            setCurrentPage(page)
        }
    }

    const handlePerRowsChange = newPerPage => {
        if (newPerPage !== perPage && filterApplied) {
            setPerPage(newPerPage)
            setCurrentPage(1)
        }
    }

    const [getDayPassReportForExport] = useLazyQuery(GET_DAYPASS_REPORT, {
        fetchPolicy: 'network-only',
        onError: error => {
            handleError(error.message)
            setExportLoading(false)
        },
    })

    const fetchBatchForExport = async (offset, limit, filterData = filterObj) => {
        try {
            const result = await getDayPassReportForExport({
                variables: {
                    page: {
                        limit,
                        offset,
                    },
                    travel_date_range: {
                        start_date: filterData.travel_date.start_date,
                        end_date: filterData.travel_date.end_date,
                    },
                },
            })

            if (result.data?.daypassReport?.success && result.data.daypassReport.code === '200') {
                return {
                    data: result.data.daypassReport.daypass_report,
                    total: result.data.daypassReport.total_count,
                    summaryTotals: result.data.daypassReport.total,
                }
            } else {
                throw new Error(result.data?.daypassReport?.message || 'Failed to fetch data')
            }
        } catch (error) {
            throw error
        }
    }

    // Create a total row for the data table
    const getTotalRow = () => {
        if (!totalValues) return null

        return {
            travel_date: 'Total',
            total_daypass: totalValues.total_daypass || 0,
            std_booking_count: totalValues.std_booking_count || 0,
            gross_revenue: totalValues.gross_revenue || 0,
            wallet_spend: totalValues.wallet_spend || 0,
            wallet_refunds: totalValues.wallet_refunds || 0,
            discounts: totalValues.discounts || 0,
            net_revenue: totalValues.net_revenue || 0,
            avg_spend_per_booking: totalValues.avg_spend_per_booking || 0,
            isTotal: true,
        }
    }

    // Modified data with total row
    const tableData = useMemo(() => {
        if (!data || data.length === 0 || !totalValues) return data
        return [...data, getTotalRow()]
    }, [data, totalValues])

    const handleExportCSV = async () => {
        try {
            setExportLoading(true)

            const currentFilterObj = { ...filterObj }

            const initialBatch = await fetchBatchForExport(0, 100, currentFilterObj)
            const totalRecords = initialBatch.total
            const summaryTotals = initialBatch.summaryTotals

            if (totalRecords === 0) {
                handleError(t('No records found to export'))
                return
            }

            let allData = [...initialBatch.data]
            let offset = 100

            while (allData.length < totalRecords) {
                const batchData = await fetchBatchForExport(offset, 100, currentFilterObj)
                allData = [...allData, ...batchData.data]
                offset += 100
            }

            const headers = [
                'Travel Date',
                'Day Pass Count',
                'Standard Bookings',
                'Gross Revenue(BHD)',
                'Wallet Spent(BHD)',
                'Wallet Refunds(BHD)',
                'Discounts(BHD)',
                'Net Revenue (BHD)',
                'Avg Spend / Booking',
            ]

            // Create the total row for CSV
            const totalRow = {
                'Travel Date': 'TOTAL',
                'Day Pass Count': summaryTotals.total_daypass || 0,
                'Standard Bookings': summaryTotals.std_booking_count || 0,
                'Gross Revenue(BHD)': (summaryTotals.gross_revenue || 0).toFixed(3),
                'Wallet Spent(BHD)': (summaryTotals.wallet_spend || 0).toFixed(3),
                'Wallet Refunds(BHD)': (summaryTotals.wallet_refunds || 0).toFixed(3),
                'Discounts(BHD)': (summaryTotals.discounts || 0).toFixed(3),
                'Net Revenue (BHD)': (summaryTotals.net_revenue || 0).toFixed(3),
                'Avg Spend / Booking': (summaryTotals.avg_spend_per_booking || 0).toFixed(3),
            }

            const exportData = allData.map(row => ({
                'Travel Date': moment(row.travel_date).format('DD MMM YYYY'),
                'Day Pass Count': row.total_daypass,
                'Standard Bookings': row.std_booking_count,
                'Gross Revenue(BHD)': (row.gross_revenue ?? 0).toFixed(3),
                'Wallet Spent(BHD)': (row.wallet_spend ?? 0).toFixed(3),
                'Wallet Refunds(BHD)': (row.wallet_refunds ?? 0).toFixed(3),
                'Discounts(BHD)': (row.discounts ?? 0).toFixed(3),
                'Net Revenue (BHD)': (row.net_revenue ?? 0).toFixed(3),
                'Avg Spend / Booking': (row.avg_spend_per_booking ?? 0).toFixed(3),
            }))

            // Add the total row to export data
            exportData.push(totalRow)

            const csvContent = [
                headers.join(','),
                ...exportData.map(row => headers.map(header => row[header]).join(',')),
            ].join('\n')

            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `daypass-report-${moment().format('YYYY-MM-DD')}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                name: t('Travel Date'),
                selector: row => row.travel_date,
                sortable: true,
                cell: row =>
                    row.isTotal ? (
                        <span className="font-bold">Total</span>
                    ) : (
                        moment(row.travel_date).format('DD MMM YYYY')
                    ),
                minWidth: '130px',
                wrap: true,
            },
            {
                name: t('Day Pass Count'),
                selector: row => row.total_daypass,
                sortable: true,
                cell: row => <span className={row.isTotal ? 'font-bold' : ''}>{row.total_daypass}</span>,
                minWidth: '100px',
                wrap: true,
            },
            {
                name: t('Standard Bookings'),
                selector: row => row.std_booking_count,
                sortable: true,
                cell: row => <span className={row.isTotal ? 'font-bold' : ''}>{row.std_booking_count}</span>,
                minWidth: '120px',
                wrap: true,
            },
            {
                name: t('Gross Revenue(BHD)'),
                selector: row => row.gross_revenue,
                sortable: true,
                cell: row => (
                    <span className={row.isTotal ? 'font-bold' : ''}>{(row.gross_revenue ?? 0).toFixed(3)}</span>
                ),
                minWidth: '160px',
                wrap: true,
            },
            {
                name: t('Wallet Spent(BHD)'),
                selector: row => row.wallet_spend,
                sortable: true,
                cell: row => (
                    <span className={row.isTotal ? 'font-bold' : ''}>{(row.wallet_spend ?? 0).toFixed(3)}</span>
                ),
                minWidth: '160px',
                wrap: true,
            },
            {
                name: t('Wallet Refunds(BHD)'),
                selector: row => row.wallet_refunds,
                sortable: true,
                cell: row => (
                    <span className={row.isTotal ? 'font-bold' : ''}>{(row.wallet_refunds ?? 0).toFixed(3)}</span>
                ),
                minWidth: '170px',
                wrap: true,
            },
            {
                name: t('Discounts(BHD)'),
                selector: row => row.discounts,
                sortable: true,
                cell: row => <span className={row.isTotal ? 'font-bold' : ''}>{(row.discounts ?? 0).toFixed(3)}</span>,
                minWidth: '140px',
                wrap: true,
            },
            {
                name: t('Net Revenue (BHD)'),
                selector: row => row.net_revenue,
                sortable: true,
                cell: row => (
                    <span className={row.isTotal ? 'font-bold' : ''}>{(row.net_revenue ?? 0).toFixed(3)}</span>
                ),
                minWidth: '170px',
                wrap: true,
            },
            {
                name: t('Avg Spend / Booking'),
                selector: row => row.avg_spend_per_booking,
                sortable: true,
                cell: row => (
                    <span className={row.isTotal ? 'font-bold' : ''}>
                        {(row.avg_spend_per_booking ?? 0).toFixed(3)}
                    </span>
                ),
                minWidth: '170px',
                wrap: true,
            },
        ],
        [t]
    )

    // Add conditional row styles for the total row
    const conditionalRowStyles = [
        {
            when: row => row.isTotal,
            style: {
                backgroundColor: '#d7e9f0',
                fontWeight: 'bold',
            },
        },
    ]

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                color: '#111827',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        pagination: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
            pageButtonsStyle: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                marginLeft: i18n.language === 'ar' ? '0' : '0.5rem',
                marginRight: i18n.language === 'ar' ? '0.5rem' : '0',
            },
        },
        noData: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
        },
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <TicketIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Day Pass Report')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Track usge of day passes')}
                            </p>
                        </div>
                    </h1>

                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 ">{t('Travel Date Range')}</label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(3, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.travel_date.start_date, filterObj.travel_date.end_date]}
                                onChange={dates => {
                                    setFilterObj({
                                        travel_date: {
                                            start_date: dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null,
                                            end_date: dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null,
                                        },
                                    })
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div className="sm:col-span-2 flex items-end gap-2">
                            <button
                                onClick={handleFilter}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Filter')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {reportLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4 -mt-3">
                        <p className="font-semibold">Day Pass Reports</p>
                        {data.length > 0 && (
                            <button
                                onClick={handleExportCSV}
                                disabled={exportLoading}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? (
                                    <>
                                        <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                        {t('Exporting...')}
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                        {t('Export to CSV')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        paginationDefaultPage={currentPage}
                        paginationRowsPerPageOptions={[10, 20, 50]}
                        paginationPerPage={perPage}
                        customStyles={customStyles}
                        conditionalRowStyles={conditionalRowStyles}
                        noDataComponent={
                            <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                                <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    className="mx-auto h-12 w-12 text-gray-400"
                                >
                                    <path
                                        d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                        strokeWidth={2}
                                        vectorEffect="non-scaling-stroke"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t('Please add or modify your search criteria and try again.')}
                                </p>
                                <div className="mt-6"></div>
                            </div>
                        }
                    />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
